/**
 * ============================================================================
 * NsatRegistrationForm — Model (form values + RHF resolver)
 * ============================================================================
 * Explicit resolver (no Yup). Degree + program required when multi-program;
 * single-program bypass lives in the controller (skips resolver).
 * ============================================================================
 */

import type { FieldErrors, Resolver } from "react-hook-form";
import {
  SELECT_DEGREE,
  SELECT_PROGRAM,
} from "@/components/molecules/Forms/ConnectModalFormIONSAT/utils";

export interface NsatRegistrationFormValues {
  fullName: string;
  emailId: string;
  countryCode: string;
  phoneNumber: string;
  program: string;
  isTermsAccepted: boolean;
  degree: string;
  // slot: string;
}

export const NATIONAL_CODE = "+91";
export const CODE = "Code";

export const COUNTRY_CODES = ["+91", "+1", "+44", "+971", "+65"];

export const degreeOptions = ["UG", "PG"];

export const errorAccordingToFieldName: Record<string, string> = {
  fullName: "Please enter valid full name",
  countryCode: "Please enter valid country code",
  phoneNumber: "Please enter valid phone number",
  emailId: "Please enter valid email address",
  program: "Please select program",
  // slot: "Please select time slot",
  degree: "Please select the degree",
};

/** Match pre-MVC NsatRegistrationForm regexes. */
export const regexForInputs: Record<string, RegExp> = {
  fullName: /^[a-zA-Z][a-zA-Z ]{2,49}$/,
  countryCode: /^\+\d{1,3}(?:-\d{1,4})?$/,
  phoneNumberIndia: /^[1-9][0-9]{9}$/,
  phoneNumberIntl: /^[1-9][0-9]{6,14}$/,
  emailId:
    /^(?!.*\.\.)[a-zA-Z0-9](\.?[a-zA-Z0-9_-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
};

/**
 * Explicit RHF resolver — matches pre-MVC checkAllFieldsValid / field checks.
 * Slot not validated (UI commented out).
 */
export function createNsatRegistrationFormResolver(): Resolver<NsatRegistrationFormValues> {
  return (values) => {
    const errors: FieldErrors<NsatRegistrationFormValues> = {};

    if (!values.fullName || !regexForInputs.fullName.test(values.fullName)) {
      errors.fullName = {
        type: "validate",
        message: errorAccordingToFieldName.fullName,
      };
    }

    if (
      !values.countryCode ||
      values.countryCode === CODE ||
      !regexForInputs.countryCode.test(values.countryCode)
    ) {
      errors.countryCode = {
        type: "validate",
        message: errorAccordingToFieldName.countryCode,
      };
    }

    const phoneRegex =
      values.countryCode !== NATIONAL_CODE
        ? regexForInputs.phoneNumberIntl
        : regexForInputs.phoneNumberIndia;
    if (!values.phoneNumber || !phoneRegex.test(String(values.phoneNumber))) {
      errors.phoneNumber = {
        type: "validate",
        message: errorAccordingToFieldName.phoneNumber,
      };
    }

    if (!values.emailId || !regexForInputs.emailId.test(values.emailId)) {
      errors.emailId = {
        type: "validate",
        message: errorAccordingToFieldName.emailId,
      };
    }

    if (!values.degree || values.degree === SELECT_DEGREE) {
      errors.degree = {
        type: "validate",
        message: errorAccordingToFieldName.degree,
      };
    }

    if (!values.program || values.program === SELECT_PROGRAM) {
      errors.program = {
        type: "validate",
        message: errorAccordingToFieldName.program,
      };
    }

    if (values.isTermsAccepted !== true) {
      errors.isTermsAccepted = {
        type: "validate",
        message: "Please accept terms & conditions",
      };
    }

    const hasErrors = Object.keys(errors).length > 0;
    return hasErrors
      ? { values: {} as Record<string, never>, errors }
      : { values, errors: {} };
  };
}

export interface NsatRegistrationFormProps {
  heading?: string;
  ctaText?: string;
  isOtpValidated?: (x: any, y?: any) => void;
  associatedPrograms?: Array<ProgramDetails>;
  setLeadFailed?: (x: boolean) => void;
  isLambdaAPI?: boolean;
  metaApiParams?: any;
  submitBtnText?: string;
  isFormDisabed: Boolean;
  setSlotDate: Function;
  testUrl: {
    current: string;
  };
}
